import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, EmptyState, Field, LoadingRows, Notice, PageHeader, Panel, TextAreaField,
} from "../components/ui.jsx";

function addressLabel(address) {
  if (!address) return "—";
  return address.name ? `${address.name} <${address.email}>` : address.email;
}

export default function EmailApprovalsPage({ onOpenLead }) {
  const selectedApproval = new URLSearchParams(window.location.search).get("approval");
  const [state, setState] = useState({ loading: true, drafts: [], settings: null, error: "" });
  const [acting, setActing] = useState(null);
  const [expandedDrafts, setExpandedDrafts] = useState(() => new Set());
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [draftData, settingsData] = await Promise.all([
        api.emailDrafts({ lifecycle: "pending_approval", limit: 100 }),
        api.emailSettings(),
      ]);
      setState({ loading: false, drafts: draftData.email_drafts, settings: settingsData.email_settings, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const drafts = useMemo(() => [...state.drafts].sort((left, right) => {
    if (left.approval?.id === selectedApproval) return -1;
    if (right.approval?.id === selectedApproval) return 1;
    return 0;
  }), [selectedApproval, state.drafts]);

  async function act(draft, action) {
    setActing(`${draft.id}:${action}`);
    setState((current) => ({ ...current, error: "" }));
    try {
      if (action === "approve") await api.approveEmail(draft.approval.id);
      else await api.cancelEmail(draft.approval.id);
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setActing(null);
    }
  }

  function toggleDraft(draftId) {
    setExpandedDrafts((current) => {
      const next = new Set(current);
      if (next.has(draftId)) next.delete(draftId);
      else next.add(draftId);
      return next;
    });
  }

  function startEditing(draft) {
    setExpandedDrafts((current) => new Set(current).add(draft.id));
    setEditing({
      draftId: draft.id,
      subject: draft.subject,
      cc: draft.cc.join(", "),
      text_body: draft.text_body,
      html_body: draft.html_body || "",
    });
  }

  async function saveEdit(draft) {
    if (!editing?.subject.trim() || !editing?.text_body.trim()) {
      setState((current) => ({ ...current, error: "Subject and plain-text body are required." }));
      return;
    }
    setActing(`${draft.id}:save`);
    setState((current) => ({ ...current, error: "" }));
    try {
      const result = await api.updateEmailDraft(draft.id, {
        subject: editing.subject,
        cc: editing.cc.split(",").map((value) => value.trim()).filter(Boolean),
        text_body: editing.text_body,
        html_body: editing.html_body || null,
      });
      setEditing(null);
      setExpandedDrafts((current) => {
        const next = new Set(current);
        next.delete(draft.id);
        next.add(result.draft.id);
        return next;
      });
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setActing(null);
    }
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Outreach"
        title="Email approvals"
        description="Review, revise, and approve agent-drafted email. Each message stays compact until you open it."
      />
      {state.error && <Notice>{state.error}</Notice>}
      {state.settings && !state.settings.delivery_ready && (
        <Notice tone="info">Delivery is not ready. Configure and enable the Cloudflare sender under Administration → Email delivery before approving a draft.</Notice>
      )}
      {state.loading ? <LoadingRows count={4} /> : !drafts.length ? (
        <EmptyState title="No emails need approval" description="Agent drafts will appear here. Creating a draft never sends it automatically." />
      ) : (
        <div className="grid gap-5">
          {drafts.map((draft) => {
            const highlighted = draft.approval?.id === selectedApproval;
            const approving = acting === `${draft.id}:approve`;
            const cancelling = acting === `${draft.id}:cancel`;
            const saving = acting === `${draft.id}:save`;
            const expanded = expandedDrafts.has(draft.id);
            const isEditing = editing?.draftId === draft.id;
            const detailsId = `email-draft-${draft.id}`;
            return (
              <Panel className={highlighted ? "ring-2 ring-primary/40" : undefined} key={draft.id}>
                <article className="grid gap-4">
                  <header className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                    <div className="grid min-w-0 gap-1">
                      <p className="text-xs text-muted-foreground">To {addressLabel(draft.to[0])}</p>
                      <h2 className="truncate text-xl font-semibold tracking-[-0.02em]">{draft.subject}</h2>
                      <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><span>{draft.person_name}{draft.company_name ? ` · ${draft.company_name}` : ""}</span>{draft.sales_motion_id && <button type="button" className="font-medium text-primary hover:text-primary-600" onClick={() => onOpenLead?.(draft.sales_motion_id)}>{draft.sales_motion_name || "Lead"} →</button>}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <Badge>{draft.person_email_verification_status}</Badge>
                      <Badge>{draft.effective_delivery_mode}</Badge>
                    </div>
                  </header>

                  <button
                    type="button"
                    className="grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-xl bg-muted px-4 py-3 text-left transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    aria-expanded={expanded}
                    aria-controls={detailsId}
                    onClick={() => toggleDraft(draft.id)}
                  >
                    <span className="grid min-w-0 gap-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Message body</span>
                      {!expanded && <span className="line-clamp-2 whitespace-pre-line text-sm leading-5 text-neutral-600">{draft.text_body}</span>}
                    </span>
                    <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      {expanded ? "Collapse" : "Expand"}
                      <span className={`text-base transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                    </span>
                  </button>

                  {expanded && (
                    <div className="grid gap-5" id={detailsId}>
                      <dl className="grid gap-px overflow-hidden rounded-xl border border-neutral-100 bg-neutral-100 text-xs sm:grid-cols-2">
                        <div className="grid gap-1 bg-background p-3"><dt className="text-muted-foreground">From</dt><dd className="break-all text-neutral-600">{addressLabel(draft.from)}</dd></div>
                        <div className="grid gap-1 bg-background p-3"><dt className="text-muted-foreground">Reply to</dt><dd className="break-all text-neutral-600">{draft.reply_to || "Sender address"}</dd></div>
                        <div className="grid gap-1 bg-background p-3"><dt className="text-muted-foreground">CC</dt><dd className="break-all text-neutral-600">{draft.cc.join(", ") || "None"}</dd></div>
                        <div className="grid gap-1 bg-background p-3"><dt className="text-muted-foreground">Approval</dt><dd className="text-neutral-600">No expiry</dd></div>
                      </dl>

                      {isEditing ? (
                        <section className="grid gap-4 rounded-xl border border-border bg-background p-4" aria-label="Edit email draft">
                          <Field
                            label="Subject"
                            value={editing.subject}
                            maxLength={500}
                            onChange={(event) => setEditing((current) => ({ ...current, subject: event.target.value }))}
                          />
                          <Field
                            label="CC"
                            hint="comma-separated"
                            value={editing.cc}
                            placeholder="name@example.com"
                            onChange={(event) => setEditing((current) => ({ ...current, cc: event.target.value }))}
                          />
                          <TextAreaField
                            label="Plain-text body"
                            className="min-h-[220px] font-sans leading-6"
                            value={editing.text_body}
                            maxLength={100000}
                            onChange={(event) => setEditing((current) => ({ ...current, text_body: event.target.value }))}
                          />
                          <TextAreaField
                            label="HTML body"
                            hint="optional"
                            className="min-h-[140px] font-mono text-xs leading-5"
                            value={editing.html_body}
                            maxLength={200000}
                            onChange={(event) => setEditing((current) => ({ ...current, html_body: event.target.value }))}
                          />
                          <p className="text-xs leading-5 text-muted-foreground">Saving retires this snapshot and creates a fresh approval for the revised message.</p>
                        </section>
                      ) : (
                        <section className="grid gap-2">
                          <h3 className="text-xs font-semibold text-muted-foreground">Plain-text body</h3>
                          <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-neutral-100 bg-muted/50 p-4 font-sans text-sm leading-6 text-neutral-600">{draft.text_body}</pre>
                        </section>
                      )}
                    </div>
                  )}

                  <footer className="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-100 pt-4">
                    <code className="max-w-full truncate font-mono text-[11px] text-muted-foreground" title={draft.payload_hash}>Payload {draft.payload_hash.slice(0, 16)}…</code>
                    <div className="flex flex-wrap justify-end gap-2">
                      {isEditing ? (
                        <>
                          <Button variant="ghost" disabled={Boolean(acting)} onClick={() => setEditing(null)}>Discard edits</Button>
                          <Button variant="primary" disabled={Boolean(acting)} onClick={() => saveEdit(draft)}>{saving ? "Saving…" : "Save changes"}</Button>
                        </>
                      ) : (
                        <>
                          <Button variant="secondary" disabled={Boolean(acting)} onClick={() => startEditing(draft)}>Edit draft</Button>
                          <Button variant="danger" disabled={Boolean(acting)} onClick={() => act(draft, "cancel")}>{cancelling ? "Cancelling…" : "Cancel draft"}</Button>
                          <Button variant="primary" disabled={Boolean(acting) || !state.settings?.delivery_ready} onClick={() => act(draft, "approve")}>{approving ? "Sending…" : "Approve and send"}</Button>
                        </>
                      )}
                    </div>
                  </footer>
                </article>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}
